import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import Pagination from "src/components/Pagination/Pagination";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import AudienceCell from "src/components/AudienceCell/AudienceCell";
import useAdminGroups from "src/features/Groups/hooks/useAdminGroups";
import useEventsPaged from "../../hooks/useEventsPaged";
import useDeleteEvent from "../../hooks/useDeleteEvent";
import useToggleEventPublished from "../../hooks/useToggleEventPublished";
import { mapEventsToContentTableItems } from "../../utils/mapEventsToContentTableItems";

type PublishedFilter = "all" | "live" | "draft";

const publishedFilterOptions: { value: PublishedFilter; label: string }[] = [
	{ value: "all", label: "All events" },
	{ value: "live", label: "Live" },
	{ value: "draft", label: "Draft" }
];

const formatDateTime = (iso?: string) => {
	if (!iso) return "—";
	const date = new Date(iso);
	if (!Number.isFinite(date.getTime())) return "—";
	return date.toLocaleDateString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};

type AudienceGroup = { id: number; name: string };

const hasAudienceGroups = (
	data: unknown
): data is { audienceGroups: AudienceGroup[] } => {
	if (typeof data !== "object" || data === null) return false;
	const audienceGroups = (data as { audienceGroups?: unknown }).audienceGroups;
	return Array.isArray(audienceGroups);
};

const eventColumns: ContentTableColumn[] = [
	{
		key: "startDate",
		label: "Start date",
		render: (item) => formatDateTime(item.data?.startDate as string)
	},
	{
		key: "endDate",
		label: "End date",
		render: (item) => formatDateTime(item.data?.endDate as string)
	},
	{
		key: "audience",
		label: "Audience",
		render: (item) => {
			const groups = hasAudienceGroups(item.data)
				? item.data.audienceGroups
				: [];
			return <AudienceCell groups={groups} />;
		}
	}
];

const publishedFilterToParam = (
	filter: PublishedFilter
): boolean | undefined => {
	if (filter === "live") return true;
	if (filter === "draft") return false;
	return undefined;
};

const Events = () => {
	const navigate = useNavigate();
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);
	const [page, setPage] = useState(1);
	const [searchInput, setSearchInput] = useState("");
	const [publishedFilter, setPublishedFilter] =
		useState<PublishedFilter>("all");
	const [filterGroupId, setFilterGroupId] = useState<
		number | "all" | "everyone"
	>("all");

	const debouncedSearch = useDebounce(searchInput, 300);

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, publishedFilter, filterGroupId]);

	const { data: adminGroups = [], isError: adminGroupsError } =
		useAdminGroups();

	// If the currently-selected group disappears from adminGroups (deleted by
	// another admin while this page is open), reset the filter so the user
	// isn't stuck filtered to an invisible option. Skip this reset when the
	// groups query has errored — adminGroups is then an empty fallback list
	// and we'd otherwise wipe the user's selection on every transient failure.
	useEffect(() => {
		if (adminGroupsError) return;
		if (typeof filterGroupId !== "number") return;
		if (!adminGroups.some((g) => Number(g.id) === filterGroupId)) {
			setFilterGroupId("all");
		}
	}, [adminGroups, filterGroupId, adminGroupsError]);

	const audienceGroupIdParam =
		typeof filterGroupId === "number" ? filterGroupId : undefined;
	const onlyUnrestrictedParam = filterGroupId === "everyone";

	const { data, isLoading, isFetching } = useEventsPaged(
		page,
		DEFAULT_ADMIN_PAGE_SIZE,
		publishedFilterToParam(publishedFilter),
		debouncedSearch,
		audienceGroupIdParam,
		onlyUnrestrictedParam
	);
	const { mutate: togglePublished } = useToggleEventPublished();
	const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteEvent();

	const totalCount = data?.count ?? 0;
	const items = mapEventsToContentTableItems(data?.results ?? []);

	const publishedFilterNode = (
		<label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
			<span>Status</span>
			<select
				value={publishedFilter}
				onChange={(e) => setPublishedFilter(e.target.value as PublishedFilter)}
				className="rounded-lg border border-border bg-white px-3 py-1.5 text-sm text-foreground shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
				aria-label="Filter events by publish status"
			>
				{publishedFilterOptions.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</label>
	);

	return (
		<Page>
			<ContentTable
				items={items}
				columns={eventColumns}
				showCreatedBy={false}
				showLastUpdated={false}
				showOrder={false}
				searchValue={searchInput}
				onSearchChange={setSearchInput}
				searchPlaceholder="Search events..."
				filterSlot={publishedFilterNode}
				isLoading={isLoading || isFetching}
				onRowClick={(item) => navigate(`/app/events/${item.id}`)}
				onMakeLive={(item) =>
					togglePublished({
						id: Number(item.id),
						published: true
					})
				}
				onMakeDraft={(item) =>
					togglePublished({
						id: Number(item.id),
						published: false
					})
				}
				onDelete={(item) => setItemToDelete(item)}
				canDelete={(item) => !item.isActive}
				createPath="/app/events/create"
				createLabel="Create Event"
				searchRowExtra={
					<div className="flex items-center gap-2 text-sm">
						<label className="flex items-center gap-2 text-muted-foreground">
							<span>Filter by group</span>
							<select
								value={filterGroupId}
								onChange={(e) => {
									const v = e.target.value;
									if (v === "all" || v === "everyone") setFilterGroupId(v);
									else setFilterGroupId(Number(v));
								}}
								className="rounded-md border border-input bg-card px-3 py-1.5 text-sm text-foreground shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
								aria-label="Filter events by group"
							>
								<option value="all">All groups</option>
								<option value="everyone">Everyone (no restrictions)</option>
								{adminGroups.map((g) => (
									<option key={g.id} value={Number(g.id)}>
										{g.name}
									</option>
								))}
							</select>
						</label>
						{adminGroupsError && (
							<span
								role="alert"
								className="text-xs text-destructive"
								title="Failed to load groups"
							>
								Couldn’t load groups
							</span>
						)}
					</div>
				}
			/>

			<Pagination
				page={page}
				pageSize={DEFAULT_ADMIN_PAGE_SIZE}
				totalCount={totalCount}
				onPageChange={setPage}
				itemLabel="events"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete event?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteEvent(Number(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Events;
